import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { nzDate } from '@promanage/core';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type SortState } from '../features/table/columns';
import { useColumnState } from '../features/table/useColumnState';
import { PropertiesNav } from '../features/property/PropertiesNav';
import {
  DEFAULT_INSPECTION_DUE_SORT,
  DEFAULT_INSPECTION_SORT,
  INSPECTION_COLUMNS,
  INSPECTION_DUE_COLUMNS,
  inspectionLate,
  type InspectionColumnId,
  type InspectionDueColumnId,
} from '../features/inspections/inspectionColumns';
import type { InspectionDueRow, InspectionRow } from '../features/inspections/types';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceChoice, workspaceTable, workspaceText } from '../features/workspaces/state';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/properties/inspections/')({
  component: InspectionsPage,
});

type Filter = 'to_book' | 'due' | 'overdue' | 'in_progress' | 'completed' | 'all';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'to_book', label: 'Due to book' },
  { value: 'due', label: 'Booked' },
  { value: 'overdue', label: 'Past its date' },
  { value: 'in_progress', label: 'Started' },
  { value: 'completed', label: 'Completed' },
  { value: 'all', label: 'All' },
];

const STORAGE_KEY = 'promanage:inspection-columns';
const DUE_STORAGE_KEY = 'promanage:inspection-due-columns';

/** One identity, so the pending state does not re-filter the list on every render. */
const NO_INSPECTIONS: InspectionRow[] = [];
const NO_DUE: InspectionDueRow[] = [];

/**
 * The inspections diary — what is booked, what is half done, and what has been signed off.
 *
 * ── THE DEFAULT FILTER IS "DUE TO BOOK", AND IT IS THE ONLY LIST HERE THAT IS NOT A RECORD ───
 *
 * Every other filter shows inspections that EXIST — somebody has already booked them. "Due to
 * book" shows the ones that do not: every tenancy whose next visit has fallen due, or is about to,
 * and which nobody has arranged. That is the failure worth a screen. An owner who finds out the
 * quarterly inspection has not happened since March is an owner who leaves, and until this list
 * existed the property that fell through the cracks was invisible on every screen in the product,
 * precisely because nothing had been booked for it — there was no row to show.
 *
 * THE DATES ARE THE ONES ON THE TENANCIES. The cycle sets each one when a visit is signed off —
 * thirteen weeks after it was conducted, by default — and a property manager may move any of them
 * from the property's Inspections tab, which is what the "moved by" note means when it appears.
 *
 * "PAST ITS DATE" IS STILL THE SHARPEST NUMBER for what HAS been booked — a visit somebody
 * arranged and did not make.
 *
 * ── "PAST ITS DATE" IS THE AGENCY'S OWN BOOKING, NOT A DEADLINE ──────────────────────────────
 *
 * There is no statutory inspection frequency for a residential tenancy; what the RTA constrains is
 * NOTICE (an inspection needs 48 hours' written notice, and no more than one in any four weeks
 * without agreement). So a visit past its scheduled date is the agency failing its own commitment,
 * which is worth chasing and is not a compliance breach — and this screen says it that way round,
 * for the reason the maintenance queue captions its service targets.
 *
 * ── TWO TABLES, ONE WORKSPACE ─────────────────────────────────────────────────────────────────
 *
 * "Due to book" is tenancies and the rest are inspections, so each has its own columns, sort and
 * remembered layout. A saved workspace keeps the filter and the layout of the table that filter
 * shows, and restores that layout to that table — the one a person was looking at when they saved.
 */
function InspectionsPage() {
  const list = useQuery(
    trpc.inspections.list.queryOptions(
      { openOnly: false },
      { meta: { errorTitle: 'Could not load the inspections' }, staleTime: 10_000 },
    ),
  );
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('to_book');
  const [sort, setSort] = useState<SortState<InspectionColumnId>>(DEFAULT_INSPECTION_SORT);
  const [dueSort, setDueSort] = useState<SortState<InspectionDueColumnId>>(
    DEFAULT_INSPECTION_DUE_SORT,
  );
  const columns = useColumnState(STORAGE_KEY, INSPECTION_COLUMNS);
  const dueColumns = useColumnState(DUE_STORAGE_KEY, INSPECTION_DUE_COLUMNS);
  const showingDue = filter === 'to_book';

  const workspace = useWorkspace({
    route: '/properties/inspections',
    state: showingDue
      ? { filters: { query, filter }, columns: dueColumns.state.order, sort: dueSort }
      : { filters: { query, filter }, columns: columns.state.order, sort },
    apply(state) {
      const saved = workspaceChoice(
        state,
        'filter',
        FILTERS.map((option) => option.value),
        'to_book',
      );
      setQuery(workspaceText(state, 'query'));
      setFilter(saved);
      if (saved === 'to_book') {
        const table = workspaceTable(state, INSPECTION_DUE_COLUMNS, DEFAULT_INSPECTION_DUE_SORT);
        dueColumns.setState(table.columns);
        setDueSort(table.sort);
      } else {
        const table = workspaceTable(state, INSPECTION_COLUMNS, DEFAULT_INSPECTION_SORT);
        columns.setState(table.columns);
        setSort(table.sort);
      }
    },
  });

  /*
   * WHAT IS DUE AND UNBOOKED. A separate query because it is a different question asked of
   * different tables — active tenancies and the agency's cadence, not the inspections table — and
   * because it is the one list here that can be empty in a good way.
   *
   * `includeBooked` stays false: a property somebody has already arranged a visit for is done as
   * far as this queue is concerned, and it appears under "Booked" instead.
   */
  const due = useQuery(
    trpc.inspections.due.queryOptions(
      { includeBooked: false },
      { meta: { errorTitle: 'Could not work out which inspections are due' }, staleTime: 30_000 },
    ),
  );

  const today = nzDate(new Date());
  const rows = list.data ?? NO_INSPECTIONS;

  const dueRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (due.data ?? NO_DUE)
      .filter(
        (row) =>
          needle === '' ||
          row.propertyAddress.toLowerCase().includes(needle) ||
          row.propertyRef.toLowerCase().includes(needle) ||
          row.tenancyRef.toLowerCase().includes(needle),
      )
      .sort((a, b) => compareRows(a, b, dueSort, INSPECTION_DUE_COLUMNS));
  }, [due.data, query, dueSort]);

  const overdueToBook = (due.data ?? NO_DUE).filter((row) => row.state === 'overdue').length;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return rows
      .filter((row) => {
        switch (filter) {
          case 'due':
            return row.state === 'scheduled';
          case 'overdue':
            return inspectionLate(row, today);
          case 'in_progress':
            return row.state === 'in_progress';
          case 'completed':
            return row.state === 'completed';
          default:
            return true;
        }
      })
      .filter(
        (row) =>
          needle === '' ||
          row.propertyAddress.toLowerCase().includes(needle) ||
          row.ref.toLowerCase().includes(needle),
      )
      .sort((a, b) => compareRows(a, b, sort, INSPECTION_COLUMNS));
  }, [rows, filter, query, today, sort]);

  const overdue = rows.filter((row) => inspectionLate(row, today)).length;

  return (
    <ListShell
      title="Inspections"
      nav={<PropertiesNav />}
      subtitle={
        showingDue
          ? overdueToBook > 0
            ? `${String(overdueToBook)} overdue and not booked`
            : 'Nothing is overdue — these fall due soon'
          : overdue > 0
            ? `${String(overdue)} past the date they were booked for`
            : 'Nothing is past the date it was booked for'
      }
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Address or reference"
      searchLabel="Search inspections"
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          {showingDue ? (
            <ColumnPicker
              columns={INSPECTION_DUE_COLUMNS}
              state={dueColumns.state}
              onChange={dueColumns.setState}
              onReset={dueColumns.reset}
            />
          ) : (
            <ColumnPicker
              columns={INSPECTION_COLUMNS}
              state={columns.state}
              onChange={columns.setState}
              onReset={columns.reset}
            />
          )}
        </>
      }
      filters={
        <FilterChips
          label="Show"
          value={filter}
          options={FILTERS}
          onChange={(next) => setFilter(next)}
        />
      }
      isPending={showingDue ? due.isPending : list.isPending}
      error={showingDue ? due.error : list.error}
      onRetry={() => void (showingDue ? due.refetch() : list.refetch())}
      isEmpty={showingDue ? dueRows.length === 0 : filtered.length === 0}
      empty={
        showingDue ? (
          <EmptyState title="Every tenancy is up to date">
            Nothing has fallen due that somebody has not already booked. Each date is set when the
            last visit was signed off, and can be moved from a property’s Inspections tab.
          </EmptyState>
        ) : (
          <EmptyState title="Nothing here">
            {filter === 'due'
              ? 'Nothing is booked. An inspection is booked from a property’s Inspections tab.'
              : 'No inspection matches that filter.'}
          </EmptyState>
        )
      }
    >
      {/*
       * A ROW OPENS THE PROPERTY'S INSPECTIONS TAB, not the report. On "Due to book" there is no
       * report yet — booking one is what the row is asking for, and the tab's form opens on that
       * date. On the rest the tab is where a visit is read in context; the ref column opens the
       * report itself.
       */}
      {showingDue ? (
        <DataTable
          rows={dueRows}
          columns={dueColumns.visible}
          sort={dueSort}
          onSortChange={setDueSort}
          rowKey={(row) => row.tenancyId}
          onOpen={(row) =>
            void navigate({
              to: '/properties/$ref',
              params: { ref: row.propertyRef },
              search: { tab: 'inspections' },
            })
          }
          caption="Tenancies whose next inspection has fallen due and which nobody has booked. Dates are the agency's own cycle; there is no statutory inspection frequency."
          minWidth="52rem"
        />
      ) : (
        <DataTable
          rows={filtered}
          columns={columns.visible}
          sort={sort}
          onSortChange={setSort}
          rowKey={(row) => row.id}
          onOpen={(row) =>
            void navigate({
              to: '/properties/$ref',
              params: { ref: row.propertyRef },
              search: { tab: 'inspections' },
            })
          }
          caption="Inspections, soonest first. A date past its booking is the agency's own appointment missed, not a statutory deadline."
          minWidth="60rem"
        />
      )}
    </ListShell>
  );
}
